import { classnames } from "../../utils/index.jsx";
import { Table } from "../UI/Table";
import moment from "moment/moment";
import {
  BalanceDomain,
  BalanceType,
  BalanceTypeCategory,
  DG_CALL_TYPES,
  RENT_CALL_TYPES,
} from "../../utils/constants/balances";
import { currencyFormatter } from "../../api/Functions";
import {
  DotsAction,
  Exclamation,
  Interrogation,
  Quittance,
  TvaIcon,
} from "../UI/Icons";
import useMediaQuery from "../../hooks/use-media-query";
import Card from "../UI/Card";
import Tooltip from "../UI/Tooltip";
import Dropdown from "../UI/Dropdown";
import DocumentDownloadLink from "../UI/DocumentDownloadLink";
import { useBalanceTypes } from "../../hooks/use-balances";
import { DocType } from "../../utils/constants/documents";
import useTenants from "../../hooks/use-tenants";
import {
  getBalanceParentId,
  isStandaloneTenantCall,
} from "../../utils/balances";
import useProperties from "../../hooks/use-properties";
import { PropertyType } from "../../models/property";
import useGroupedBalances from "../../hooks/use-grouped-balances";
import ButtonAction from "../UI/ButtonAction";
import ButtonCard from "../UI/ButtonCard";
// @ts-expect-error pure js
import useCanWrite from "../../hooks/use-can-write";
import { READ_ONLY_MESSAGE } from "../../utils/permissions";

function gateAction(node, canWrite) {
  return canWrite ? (
    node
  ) : (
    <Tooltip content={READ_ONLY_MESSAGE}>{node}</Tooltip>
  );
}

function TVAInfo({ balance }) {
  if (balance.taux_tva === 0) return null;
  const montantHT = currencyFormatter(Math.abs(balance.montant_ht));
  const montantTVA = currencyFormatter(Math.abs(balance.montant_tva));
  return (
    <Tooltip content={`${montantHT} HT | TVA : ${montantTVA}`}>
      <TvaIcon />
    </Tooltip>
  );
}

export default function BalanceList({
  balances,
  quittances,
  domaineBalance,
  parentId,
  isTenant,
  isLocataire,
  onUpdate,
  onCreate,
  onDuplicate,
  onDelete,
  propertyType,
}) {
  const isSmallScreen = useMediaQuery("(max-width: 650px)");
  const { balanceTypes, balanceTypesByCategory, isLoading } = useBalanceTypes();
  const { tenantsById } = useTenants({ withById: true });
  const { propertiesById } = useProperties({ withById: true });
  const groupedBalances = useGroupedBalances(
    balances,
    domaineBalance,
    parentId,
  );
  const canWrite = useCanWrite();

  if (isLoading) return null;
  let totalDebit = 0;
  let totalDebitDG = 0;
  let totalCredit = 0;
  let totalCreditDG = 0;
  let balanceTotal = 0;

  async function markAsPaid(balance) {
    try {
      const appelStaticIds = balanceTypesByCategory[
        BalanceTypeCategory.LOCATAIRE_APPEL
      ].map((balanceType) => balanceType.static_id);

      const isAgencyFee = (type) =>
        balanceTypes?.[type]?.is_agency_fee ?? false;
      let newBalance = { ...balance };
      delete newBalance.id;

      if (DG_CALL_TYPES.includes(balance.type)) {
        newBalance.montant = -balance.montant;
        newBalance.type = BalanceType.PAIEMENT_DG;
      } else if (isAgencyFee(balance.type)) {
        // Les honoraires dus à l'agence se règlent à part du loyer.
        newBalance.montant = -balance.montant;
        newBalance.type = BalanceType.PAIEMENT_HONORAIRES;
      } else if (isStandaloneTenantCall(balance, balanceTypes)) {
        newBalance.montant = -balance.montant;
        newBalance.type = BalanceType.PAIEMENT;
      } else {
        newBalance.montant = balances
          .filter(
            (balance) =>
              moment(balance.date_transac).isSame(
                newBalance.date_transac,
                "month",
              ) &&
              appelStaticIds.includes(balance.type) &&
              !isAgencyFee(balance.type),
          )
          .reduce((accumulator, current) => {
            return accumulator - current.montant;
          }, 0);
        newBalance.type = BalanceType.PAIEMENT;
      }
      await onCreate(newBalance);
    } catch (error) {
      console.error(error);
    }
  }

  const canMarkAsPaid = (balance) =>
    [...RENT_CALL_TYPES, ...DG_CALL_TYPES].includes(balance.type) ||
    isStandaloneTenantCall(balance, balanceTypes);

  async function duplicate(balanceId) {
    try {
      await onDuplicate(balanceId);
    } catch (error) {
      console.error(error);
    }
  }

  async function deleteHandler(
    balanceId,
    domaine,
    parentId,
    type,
    isGrouped = false,
  ) {
    if (
      window.confirm(
        [
          ...RENT_CALL_TYPES,
          BalanceType.APPEL_CHARGE,
          BalanceType.APPEL_COMPLEMENTAIRE,
        ].includes(type)
          ? "Attention ! Les appels de loyer / charge ne devraient pas être " +
              "supprimés ! Ils sont absolument nécessaires pour la génération des " +
              "quittances et la bonne tenue de la comptabilité locataire " +
              "(détection loyer à jour, etc). Vous pouvez changer le montant en " +
              "cas de modification du prorata de l’arrivée ou du départ du " +
              "locataire. Pour enregistrer un paiement de loyer, vous devez " +
              "ajouter une nouvelle opération «paiement» ou cliquer sur " +
              "les «•••» du mois concerné et «marquer comme payé»."
          : "Voulez-vous vraiment supprimer cette entrée ?",
      )
    )
      try {
        await onDelete(balanceId, domaine, parentId, isGrouped);
      } catch (error) {
        console.error(error);
      }
  }

  const folderWarning = (
    <Tooltip
      content={
        <>
          Cette balance est associée au dossier mais devrait être répartie sur
          les biens contenus dedans. Editez la balance afin de la repartir et
          supprimer automatiquement celle associée au dossier.
        </>
      }
    >
      <Exclamation size={16} className="marginL5" />
    </Tooltip>
  );

  const isFutureDate = (date) => moment(date).isAfter(moment(), "day");

  function BalanceListSmallScreen({
    balance,
    balanceText,
    isGroupedBalance,
    isBalanceFromSplit,
    canWrite,
  }) {
    return (
      <Dropdown
        header={
          <ButtonCard
            className={classnames(
              "marginB5 w-100",
              isFutureDate(balance.date_transac) && "bg-grey",
            )}
          >
            <div className={"flex space-between"}>
              <p className="secondaryText text-left">
                {moment(balance.date_transac).format("DD/MM/YYYY")}
              </p>
            </div>
            <div
              className={classnames(
                "secondaryText flex space-between",
                balance.montant > 0 ? "green" : "blue",
              )}
            >
              <p
                className={classnames(
                  "secondaryText",
                  balance.montant > 0 ? "green" : "blue",
                )}
              >
                {balanceText}
                {(balance.comment || balance.description) && (
                  <Tooltip
                    content={balance.comment || balance.description}
                    direction="bottom"
                  >
                    <Interrogation className={"marginL5"} />
                  </Tooltip>
                )}
                {propertyType === PropertyType.BIEN_NON_COMPTABLE &&
                  domaineBalance === BalanceDomain.FOLDER &&
                  balance.domaine === BalanceDomain.FOLDER &&
                  !isGroupedBalance &&
                  folderWarning}
              </p>
              <p
                className={classnames(
                  "secondaryText",
                  balance.montant > 0 ? "green" : "blue",
                )}
              >
                {currencyFormatter(balance.montant, { withSign: true })}
                <TVAInfo balance={balance} />
              </p>
            </div>
          </ButtonCard>
        }
        type={"click"}
        body={
          <>
            {isTenant &&
              canMarkAsPaid(balance) &&
              gateAction(
                <div
                  style={{
                    color: "green",
                    ...(!canWrite && {
                      cursor: "not-allowed",
                      opacity: 0.5,
                    }),
                  }}
                  onClick={
                    canWrite
                      ? async () => {
                          await markAsPaid(balance);
                        }
                      : undefined
                  }
                  className={"secondaryText"}
                >
                  Marquer comme payé
                </div>,
                canWrite,
              )}
            {gateAction(
              <div
                style={
                  !canWrite
                    ? { cursor: "not-allowed", opacity: 0.5 }
                    : undefined
                }
                onClick={canWrite ? () => onUpdate(balance) : undefined}
                className={"secondaryText"}
              >
                Editer
              </div>,
              canWrite,
            )}
            {gateAction(
              <div
                style={
                  !canWrite
                    ? { cursor: "not-allowed", opacity: 0.5 }
                    : undefined
                }
                onClick={canWrite ? () => duplicate(balance.id) : undefined}
                className={"secondaryText"}
              >
                Dupliquer
              </div>,
              canWrite,
            )}
            {!isBalanceFromSplit &&
              gateAction(
                <div
                  style={{
                    color: "red",
                    ...(!canWrite && {
                      cursor: "not-allowed",
                      opacity: 0.5,
                    }),
                  }}
                  onClick={
                    canWrite
                      ? () =>
                          deleteHandler(
                            isGroupedBalance
                              ? balance.correlation_id
                              : balance.id,
                            balance.domaine,
                            getBalanceParentId(balance),
                            balance.type,
                            isGroupedBalance,
                          )
                      : undefined
                  }
                  className={"secondaryText "}
                >
                  Supprimer
                </div>,
                canWrite,
              )}
          </>
        }
      />
    );
  }

  function BalanceListBigScreen({
    balance,
    balanceText,
    quittance,
    isGroupedBalance,
    isBalanceFromSplit,
    totalCredit,
    totalDebit,
    canWrite,
  }) {
    return (
      <Table.Row
        className={classnames(isFutureDate(balance.date_transac) && "bg-grey")}
      >
        <Table.Cell className="secondaryText">
          {moment(balance.date_transac).format("DD/MM/YYYY")}
        </Table.Cell>
        <Table.Cell
          className={classnames(
            "secondaryText",
            balance.montant > 0 ? "green" : "blue",
          )}
        >
          {balanceText}
          {(balance.comment || balance.description) && (
            <Tooltip
              content={balance.comment || balance.description}
              direction="bottom"
            >
              <Interrogation className={"marginL5"} />
            </Tooltip>
          )}
          {propertyType === PropertyType.BIEN_NON_COMPTABLE &&
            domaineBalance === BalanceDomain.FOLDER &&
            balance.domaine === BalanceDomain.FOLDER &&
            !isGroupedBalance &&
            folderWarning}
        </Table.Cell>
        <Table.Cell align="right" className="secondaryText blue">
          {balance.montant <= 0 && (
            <>
              {currencyFormatter(balance.montant, { withSign: true })}
              <TVAInfo balance={balance} />
            </>
          )}
        </Table.Cell>
        <Table.Cell align="right" className="secondaryText green">
          {balance.montant > 0 && (
            <>
              {currencyFormatter(balance.montant, { withSign: true })}
              <TVAInfo balance={balance} />
            </>
          )}
        </Table.Cell>
        {isTenant && (
          <Table.Cell align="right" className="secondaryText">
            {currencyFormatter(totalCredit + totalDebit, {
              withSign: true,
            })}
          </Table.Cell>
        )}
        <Table.Cell className="secondaryText">
          <div className={"flex gap10 align-items-center justify-content-end"}>
            {isTenant &&
              RENT_CALL_TYPES.includes(balance.type) &&
              quittance && (
                <DocumentDownloadLink
                  className={"marginL5"}
                  documentId={quittance.id}
                  file={quittance}
                  save
                >
                  <Quittance stroke={"#9D9D9D"} size={18} />
                </DocumentDownloadLink>
              )}
            {balance.file && (
              <DocumentDownloadLink
                className={"marginL5"}
                documentId={
                  isGroupedBalance ? balance.balances[0].id : balance.id
                }
                file={balance.file}
                doctype={DocType.JUSTIFICATIF_BALANCE}
              >
                <Quittance stroke={"#9D9D9D"} size={18} />
              </DocumentDownloadLink>
            )}
            {!isLocataire && (
              <>
                <Dropdown
                  header={
                    <ButtonAction
                      logo={<DotsAction />}
                      tooltip={"Plus d'actions"}
                    />
                  }
                  type={"click"}
                  body={
                    <>
                      {gateAction(
                        <div
                          style={
                            !canWrite
                              ? { cursor: "not-allowed", opacity: 0.5 }
                              : undefined
                          }
                          onClick={
                            canWrite ? () => onUpdate(balance) : undefined
                          }
                          className={"secondaryText"}
                        >
                          Editer
                        </div>,
                        canWrite,
                      )}
                      {isTenant &&
                        canMarkAsPaid(balance) &&
                        gateAction(
                          <div
                            style={{
                              color: "green",
                              ...(!canWrite && {
                                cursor: "not-allowed",
                                opacity: 0.5,
                              }),
                            }}
                            onClick={
                              canWrite
                                ? async () => {
                                    await markAsPaid(balance);
                                  }
                                : undefined
                            }
                            className={"secondaryText"}
                          >
                            Marquer comme payé
                          </div>,
                          canWrite,
                        )}
                      {gateAction(
                        <div
                          style={
                            !canWrite
                              ? { cursor: "not-allowed", opacity: 0.5 }
                              : undefined
                          }
                          onClick={
                            canWrite ? () => duplicate(balance.id) : undefined
                          }
                          className={"secondaryText"}
                        >
                          Dupliquer
                        </div>,
                        canWrite,
                      )}
                      {!isBalanceFromSplit &&
                        gateAction(
                          <div
                            style={{
                              color: "red",
                              ...(!canWrite && {
                                cursor: "not-allowed",
                                opacity: 0.5,
                              }),
                            }}
                            onClick={
                              canWrite
                                ? () =>
                                    deleteHandler(
                                      isGroupedBalance
                                        ? balance.correlation_id
                                        : balance.id,
                                      balance.domaine,
                                      getBalanceParentId(balance),
                                      balance.type,
                                      isGroupedBalance,
                                    )
                                : undefined
                            }
                            className={"secondaryText "}
                          >
                            Supprimer
                          </div>,
                          canWrite,
                        )}
                    </>
                  }
                />
              </>
            )}
          </div>
        </Table.Cell>
      </Table.Row>
    );
  }

  const rows = groupedBalances
    .reverse()
    .map((balance, i) => {
      if (
        balanceTypesByCategory[BalanceTypeCategory.DG_CREDIT]
          .map((balanceType) => balanceType.static_id)
          .includes(balance.type) ||
        balanceTypesByCategory[BalanceTypeCategory.DG_DEBIT]
          .map((balanceType) => balanceType.static_id)
          .includes(balance.type)
      ) {
        if (balance.montant <= 0) {
          // eslint-disable-next-line react-hooks/immutability
          totalDebitDG += balance.montant;
        } else {
          totalCreditDG += balance.montant;
        }
      }

      if (balance.montant <= 0) {
        totalDebit += balance.montant;
      } else {
        totalCredit += balance.montant;
      }
      balanceTotal += balance.montant;

      const quittance = quittances?.find((quittance) =>
        moment(quittance.meta.date_quittance, "YYYY-MM-DD").isSame(
          balance.date_transac,
          "month",
        ),
      );

      let balanceText = balanceTypes[balance.type].text;
      if (
        RENT_CALL_TYPES.includes(balance.type) ||
        balance.type === BalanceType.APPEL_CHARGE
      ) {
        balanceText = `${balanceText} ${moment(balance.date_transac).format(
          "MMMM",
        )}`;
      }
      if ([BalanceDomain.FOLDER].includes(domaineBalance)) {
        const propertyName = propertiesById[balance.id_bien]?.nom;
        if (propertyName) {
          balanceText = `${balanceText} (${propertyName})`;
        }
      }
      if (
        [BalanceDomain.PROPERTY, BalanceDomain.FOLDER].includes(
          domaineBalance,
        ) &&
        [
          BalanceType.PAIEMENT,
          BalanceType.PAIEMENT_DG,
          BalanceType.PAIEMENT_HONORAIRES,
          BalanceType.APPEL_DG,
        ].includes(balance.type)
      ) {
        const tenantName = tenantsById[balance.id_tenant]?.nom;
        if (tenantName) {
          balanceText = `${balanceText} ${tenantName}`;
        }
      }

      const isGroupedBalance = balance.balances?.length > 0;
      const isBalanceFromSplit =
        balance && balance.correlation_id && !isGroupedBalance;

      return isSmallScreen ? (
        <BalanceListSmallScreen
          key={i}
          balance={balance}
          balanceText={balanceText}
          isBalanceFromSplit={isBalanceFromSplit}
          isGroupedBalance={isGroupedBalance}
          quittance={quittance}
          totalCredit={totalCredit}
          totalDebit={totalDebit}
          canWrite={canWrite}
        />
      ) : (
        <BalanceListBigScreen
          key={i}
          balance={balance}
          balanceText={balanceText}
          isBalanceFromSplit={isBalanceFromSplit}
          isGroupedBalance={isGroupedBalance}
          quittance={quittance}
          totalCredit={totalCredit}
          totalDebit={totalDebit}
          canWrite={canWrite}
        />
      );
    })
    .reverse();

  return (
    <>
      {isSmallScreen ? (
        <>
          {!isLocataire && (
            <Card className={"marginB10"}>
              <p className={"text-right primaryText"}>
                Total débit :{" "}
                {currencyFormatter(totalCredit, { withSign: true })}
              </p>
              <p className={"text-right primaryText"}>
                Total crédit :{" "}
                {currencyFormatter(totalDebit, { withSign: true })}
              </p>
            </Card>
          )}
          {rows}
        </>
      ) : (
        <Card className={classnames("w-100")}>
          <div
            className={
              "flex wrap gap10 space-between align-items-center marginB20"
            }
          >
            <p className={"cardTitle"}>Balance</p>
          </div>
          <Table>
            <Table.Header>
              <Table.Column>Date</Table.Column>
              <Table.Column>Nature de l'opération</Table.Column>

              {isSmallScreen ? (
                <Table.Column>Montant</Table.Column>
              ) : (
                <>
                  <Table.Column align="right">Débit</Table.Column>
                  <Table.Column align="right">Crédit</Table.Column>
                  {isTenant && <Table.Column align="right">Solde</Table.Column>}
                </>
              )}
              <Table.Column align="right"></Table.Column>
            </Table.Header>
            <Table.Body>
              <Table.Row className={"primaryText"}>
                <Table.Cell></Table.Cell>
                <Table.Cell>TOTAL</Table.Cell>
                {isSmallScreen ? (
                  <Table.Cell colSpan={"2"}>
                    {currencyFormatter(totalCredit, { withSign: true })}
                  </Table.Cell>
                ) : (
                  <>
                    <Table.Cell align="right">
                      {currencyFormatter(totalDebit, { withSign: true })}
                    </Table.Cell>
                    <Table.Cell align="right">
                      {currencyFormatter(totalCredit, { withSign: true })}
                    </Table.Cell>
                    {isTenant && (
                      <Table.Cell align="right">
                        {currencyFormatter(balanceTotal, { withSign: true }) ??
                          "0€"}
                      </Table.Cell>
                    )}
                  </>
                )}
                <Table.Cell></Table.Cell>
              </Table.Row>
              {!isTenant && (
                <Table.Row>
                  <Table.Cell></Table.Cell>
                  <Table.Cell>Dont dépôt garantie</Table.Cell>
                  {isSmallScreen ? (
                    <Table.Cell colSpan={"2"}>
                      {currencyFormatter(totalCreditDG, { withSign: true })}
                    </Table.Cell>
                  ) : (
                    <>
                      <Table.Cell align="right">
                        {currencyFormatter(totalDebitDG, { withSign: true })}
                      </Table.Cell>
                      <Table.Cell align="right">
                        {currencyFormatter(totalCreditDG, { withSign: true })}
                      </Table.Cell>
                    </>
                  )}
                  <Table.Cell></Table.Cell>
                </Table.Row>
              )}
              {rows}
            </Table.Body>
          </Table>
        </Card>
      )}
    </>
  );
}
